import React, { useCallback, useMemo, useRef, useState } from "react";
import Plotly from "plotly.js-basic-dist";
import Plot from "./PlotlyChart";
import LegendControls from "./LegendControls";
import {
  getWeightLengthSeriesColor,
  sortSpeciesForWeightLengthLegend,
  tagIdFromSpeciesLabel,
} from "../../graphView/colors";
import { buildLengthTicks, buildWeightTicks } from "./axisTicks";
import { plotlyAxisBase, plotlyAxisTitle } from "./plotlyAxisTheme";
import { plotlyHorizontalLegendForTraces } from "./plotlyLegendLayout";
import { useGraphV2PlotHeight } from "../ChartFullscreenShell";
import { useSamplingPlotExportRegister } from "../../graphView/useSamplingPlotExportRegister";
import "./wrVsLengthPlotly.css";

const CARD_W = 280;
const CARD_H = 300;
function formatSignedFixed(n, decimals) {
  const s = n.toFixed(decimals);
  if (n > 0) return `+${s}`;
  return s;
}

/** One legend entry per series showing combined sampling+stocking count. */
function actualWeightTraceName(species, sampLen, stockLen) {
  const n = sampLen + stockLen;
  if (tagIdFromSpeciesLabel(species)) {
    // For tags, just show tag and total count
    return `${species} (${n})`;
  }
  // For species, show breakdown if both types exist
  if (sampLen > 0 && stockLen > 0) {
    return `${species} (${n}: ${sampLen} sampled, ${stockLen} stocked)`;
  }
  return `${species} (${n})`;
}

/** Stable fish-type label for standard-weight curves (not overwritten in tag mode). */
function fishTypeLabelFromRow(d) {
  if (d.fishTypeName) return d.fishTypeName;
  const sp = d.species;
  if (
    sp &&
    !tagIdFromSpeciesLabel(sp) &&
    sp !== "Tagged Fish" &&
    sp !== "Untagged Fish"
  ) {
    return sp;
  }
  return null;
}

export default function WeightVsLengthChart({
  graphData,
  selectedUnit,
  selectedFish,
  isMobile,
}) {
  const wrapRef = useRef(null);
  const plotRef = useRef(null);
  useSamplingPlotExportRegister(plotRef);
  const [tip, setTip] = useState(null);

  const speciesList = useMemo(() => {
    const raw = [...new Set(graphData.map((d) => d.species))];
    return sortSpeciesForWeightLengthLegend(raw);
  }, [graphData]);

  const lengthUnit = selectedUnit.value === "English" ? "Inch" : "mm";
  const weightUnit = selectedUnit.value === "English" ? "Pound" : "gram";
  const lengthTitle = `Length (${selectedUnit.value === "English" ? "Inch" : "mm"})`;
  const weightTitle = `Weight (${selectedUnit.value === "English" ? "Pound" : "gram"})`;

  const traces = useMemo(() => {
    const t = [];
    speciesList.forEach((species) => {
      const rows = graphData
        .filter((d) => d.species === species)
        .sort((a, b) => a.length - b.length);
      const samp = rows.filter((d) => !d.isStocking);
      const stock = rows.filter((d) => d.isStocking);
      const colorOrObj = getWeightLengthSeriesColor(species, speciesList);
      // For tags: colorOrObj is {color, shape}; for species: it's a string
      const seriesColor = typeof colorOrObj === "string" ? colorOrObj : colorOrObj.color;
      const samplingShape = typeof colorOrObj === "string" ? "circle" : colorOrObj.shape;
      
      const allActual = [...samp, ...stock].sort((a, b) => a.length - b.length);
      if (allActual.length) {
        t.push({
          type: "scatter",
          mode: "markers",
          name: actualWeightTraceName(species, samp.length, stock.length),
          x: allActual.map((r) => r.length),
          y: allActual.map((r) => r.actualWeight),
          marker: {
            color: seriesColor,
            opacity: 0.85,
            size: 9,
            // Use same assigned shape for both sampling and stocking
            symbol: samplingShape,
            line: {
              color: "white",
              width: 1.5,
            },
          },
          customdata: allActual.map((r) => [
            r.species,
            Number(r.standardWeight || 0),
            !!r.isStocking,
          ]),
          hovertemplate: "<extra></extra>",
        });
      }
    });

    const fishTypeSet = new Set();
    graphData.forEach((d) => {
      const label = fishTypeLabelFromRow(d);
      if (label) fishTypeSet.add(label);
    });
    const fishTypesOrdered = sortSpeciesForWeightLengthLegend([...fishTypeSet]);

    fishTypesOrdered.forEach((fishType) => {
      const allTypeRows = graphData.filter(
        (d) => fishTypeLabelFromRow(d) === fishType
      );
      if (allTypeRows.length === 0) return;

      // Include stocked lengths on the standard curve (e.g. small stocked fish) while
      // preferring sampled standard weight when the same length appears twice.
      const curveRowsOrdered = [
        ...allTypeRows.filter((d) => !d.isStocking),
        ...allTypeRows.filter((d) => d.isStocking),
      ];

      const byLen = new Map();
      curveRowsOrdered.forEach((r) => {
        const L = Number(r.length);
        const sw = Number(r.standardWeight);
        if (!Number.isFinite(L) || !Number.isFinite(sw)) return;
        if (!byLen.has(L)) byLen.set(L, sw);
      });
      const lens = [...byLen.keys()].sort((a, b) => a - b);
      if (lens.length === 0) return;
      const std = lens.map((L) => byLen.get(L));
      let xLine = lens;
      let yLine = std;
      if (lens.length === 1) {
        xLine = [lens[0], lens[0]];
        yLine = [std[0], std[0]];
      }
      const ftColor = getWeightLengthSeriesColor(fishType, fishTypesOrdered);
      const lineColor = typeof ftColor === "string" ? ftColor : ftColor.color;
      t.push({
        type: "scatter",
        mode: "lines",
        name: `${fishType} Standard`,
        x: xLine,
        y: yLine,
        line: { color: lineColor, width: 2 },
        hoverinfo: "skip",
      });
    });

    return t;
  }, [graphData, speciesList]);

  const legendLayout = useMemo(
    () => plotlyHorizontalLegendForTraces(traces, isMobile),
    [traces, isMobile]
  );

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: 500,
    desktopDefault: 700,
    legendExtra: legendLayout.extraHeight || 0,
  });

  const xTicks = useMemo(
    () =>
      buildLengthTicks(
        graphData.map((d) => Number(d.length || 0)),
        selectedUnit.value
      ),
    [graphData, selectedUnit.value]
  );
  const yTicks = useMemo(
    () =>
      buildWeightTicks(
        graphData.map((d) =>
          Number(Math.max(d.actualWeight || 0, d.standardWeight || 0))
        ),
        selectedUnit.value,
        20
      ),
    [graphData, selectedUnit.value]
  );

  const xAxisLayout = useMemo(
    () => ({
      title: plotlyAxisTitle(lengthTitle, isMobile),
      zeroline: false,
      tickmode: "array",
      tickvals: xTicks,
      range: [xTicks[0] || 0, xTicks[xTicks.length - 1] || 0],
      tickangle: isMobile ? -45 : 0,
      ...plotlyAxisBase(isMobile),
    }),
    [lengthTitle, isMobile, xTicks]
  );

  const yAxisLayout = useMemo(
    () => ({
      title: plotlyAxisTitle(weightTitle, isMobile),
      rangemode: "tozero",
      tickmode: "array",
      tickvals: yTicks,
      range: [0, yTicks[yTicks.length - 1] || 0],
      ...plotlyAxisBase(isMobile),
    }),
    [weightTitle, isMobile, yTicks]
  );

  const onHover = useCallback(
    (eventData) => {
      const pt = eventData?.points?.[0];
      const el = wrapRef.current;
      const evt = eventData?.event;
      if (!pt || el == null || typeof pt.x !== "number" || typeof pt.y !== "number") {
        return;
      }
      const traceMode = pt.data?.mode || pt.fullData?.mode;
      if (traceMode !== "markers") {
        return;
      }
      let cx;
      let cy;
      if (evt && typeof evt.clientX === "number" && typeof evt.clientY === "number") {
        cx = evt.clientX;
        cy = evt.clientY;
      } else if (pt.bbox) {
        cx = (pt.bbox.x0 + pt.bbox.x1) / 2;
        cy = pt.bbox.y0;
      } else {
        return;
      }
      const cd = pt.customdata;
      const species =
        typeof cd === "string"
          ? cd
          : Array.isArray(cd)
            ? String(cd[0] ?? "")
            : "";
      const standardWeight = Array.isArray(cd) ? Number(cd[1]) : NaN;
      const isStocking = Array.isArray(cd) && cd[2] === true;
      const length = pt.x;
      const actualWeight = pt.y;
      const diff = actualWeight - standardWeight;
      const pct = standardWeight > 0 ? (actualWeight / standardWeight - 1) * 100 : 0;
      const aboveStandard = actualWeight >= standardWeight;

      const rect = el.getBoundingClientRect();
      const pad = 12;
      let left = cx - rect.left + pad;
      let top = cy - rect.top + pad;
      if (left + CARD_W > rect.width - 8) {
        left = cx - rect.left - CARD_W - pad;
      }
      if (top + CARD_H > rect.height - 8) {
        top = cy - rect.top - CARD_H - pad;
      }
      left = Math.max(8, Math.min(left, rect.width - CARD_W - 8));
      top = Math.max(8, Math.min(top, rect.height - CARD_H - 8));

      const diffClass =
        diff > 1e-6
          ? "wr-length-hover-card__value--diff-pos"
          : diff < -1e-6
            ? "wr-length-hover-card__value--diff-neg"
            : "wr-length-hover-card__value--diff-zero";

      setTip({
        left,
        top,
        species,
        length,
        actualWeight,
        standardWeight,
        diff,
        pct,
        diffClass,
        aboveStandard,
        isStocking,
      });
    },
    []
  );

  const onUnhover = useCallback(() => setTip(null), []);

  const handleSelectAll = useCallback(() => {
    if (plotRef.current && plotRef.current.el) {
      const update = { visible: traces.map(() => true) };
      const indices = traces.map((_, i) => i);
      Plotly.restyle(plotRef.current.el, update, indices);
    }
  }, [traces]);

  const handleDeselectAll = useCallback(() => {
    if (plotRef.current && plotRef.current.el) {
      const update = {
        visible: traces.map((tr) =>
          tr.mode === "lines" ? true : "legendonly"
        ),
      };
      const indices = traces.map((_, i) => i);
      Plotly.restyle(plotRef.current.el, update, indices);
    }
  }, [traces]);

  return (
    <div
      ref={wrapRef}
      className="weight-vs-length-plotly-wrap"
      style={{ position: "relative", width: "100%" }}
    >
      <LegendControls
        onSelectAll={handleSelectAll}
        onDeselectAll={handleDeselectAll}
      />
      <Plot
        ref={plotRef}
        data={traces}
        layout={{
          autosize: true,
          height: plotHeight,
          margin: { t: legendLayout.marginTop, r: 40, b: isMobile ? 110 : 90, l: 80 },
          paper_bgcolor: "white",
          plot_bgcolor: "white",
          xaxis: xAxisLayout,
          yaxis: yAxisLayout,
          legend: legendLayout.legend,
          hovermode: "closest",
          hoverdistance: 8,
        }}
        style={{ width: "100%" }}
        useResizeHandler
        onHover={onHover}
        onUnhover={onUnhover}
      />
      {tip && (
        <div
          className={`wr-length-hover-card wr-length-hover-card--weight${
            isMobile ? " wr-length-hover-card--mobile" : ""
          }`}
          style={{ left: tip.left, top: tip.top }}
          role="tooltip"
        >
          <div className="wr-length-hover-card__body">
            <p className="wr-length-hover-card__title">
              {tip.species}
              {tip.isStocking ? " · Stocked" : ""}
            </p>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Length:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--length">
                {tip.length.toFixed(2)} {lengthUnit}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Actual Weight:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--actual">
                {tip.actualWeight.toFixed(2)} {weightUnit}
              </span>
            </div>
            <div className="wr-length-hover-card__row">
              <span className="wr-length-hover-card__label">Standard Weight:</span>
              <span className="wr-length-hover-card__value wr-length-hover-card__value--standard">
                {Number.isFinite(tip.standardWeight)
                  ? `${tip.standardWeight.toFixed(2)} ${weightUnit}`
                  : "—"}
              </span>
            </div>
            <div className="wr-length-hover-card__subgrid">
              <div className="wr-length-hover-card__row">
                <span className="wr-length-hover-card__label">Difference:</span>
                <span className={`wr-length-hover-card__value ${tip.diffClass}`}>
                  {Number.isFinite(tip.diff)
                    ? `${formatSignedFixed(tip.diff, 2)} ${weightUnit}`
                    : "—"}
                </span>
              </div>
              <div className="wr-length-hover-card__row">
                <span className="wr-length-hover-card__label">Percentage:</span>
                <span className={`wr-length-hover-card__value ${tip.diffClass}`}>
                  {Number.isFinite(tip.pct) ? `${formatSignedFixed(tip.pct, 1)}%` : "—"}
                </span>
              </div>
            </div>
          </div>
          <div
            className={
              tip.aboveStandard
                ? "wr-length-hover-card__badge wr-length-hover-card__badge--ok"
                : "wr-length-hover-card__badge wr-length-hover-card__badge--low"
            }
          >
            {tip.aboveStandard
              ? "✓ At or above standard weight for this length"
              : "Below standard weight for this length"}
          </div>
        </div>
      )}
    </div>
  );
}
